import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useOrganization } from "@clerk/clerk-react";
import { ArrowLeft, Check, Copy, Rocket, ShieldCheck, Wrench, Code2, Lightbulb, KeyRound } from "lucide-react";
import toast from "react-hot-toast";

const BASE_URL = "https://api.uttik.com";

function CodeBlock({ code, id, copied, setCopied, label }) {
  return (
    <div className="rounded-xl bg-[#0f172a] border border-white/5 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/5">
        <span className="text-[11px] font-bold uppercase tracking-wide text-gray-400">{label || "Code"}</span>
        <button
          type="button"
          title="Copy"
          onClick={() => {
            navigator.clipboard.writeText(code);
            setCopied(id);
            toast.success("Copied!");
            setTimeout(() => setCopied(null), 2000);
          }}
          className="p-1 rounded hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
        >
          {copied === id ? <Check className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </div>
      <pre className="p-3 text-[11px] leading-relaxed text-gray-200 overflow-x-auto">
        <code>{code}</code>
      </pre>
    </div>
  );
}

const LANGS = ["cURL", "JavaScript", "Python", "PHP"];

/** Builds a code sample for the given task + language. */
function buildSample(task, lang, orgId) {
  const key = "utsk_your_key_here";
  const org = orgId || "org_your_org_id";

  const tasks = {
    listFaqs: {
      method: "GET",
      path: "/api/faqs/list?page=1&per_page=10",
      auth: true,
    },
    createFaq: {
      method: "POST",
      path: "/api/faqs/create",
      auth: true,
      body: {
        question: "How do I reset my password?",
        answer: "Go to Settings → Security → Reset Password.",
        status: "public",
        tags: ["account"],
      },
    },
    listTickets: {
      method: "GET",
      path: "/api/support/queries?limit=30",
      auth: true,
    },
    askChat: {
      method: "POST",
      path: "/api/chat/query/public",
      auth: false,
      body: {
        organization_id: org,
        query: "How do I reset my password?",
        chat_history: [],
      },
    },
  };

  const t = tasks[task];
  const url = `${BASE_URL}${t.path}`;
  const bodyStr = t.body ? JSON.stringify(t.body, null, 2) : null;

  if (lang === "cURL") {
    let out = `curl -X ${t.method} "${url}"`;
    if (t.auth) out += ` \\\n  -H "X-API-KEY: ${key}"`;
    if (bodyStr) {
      out += ` \\\n  -H "Content-Type: application/json"`;
      out += ` \\\n  -d '${JSON.stringify(t.body)}'`;
    }
    return out;
  }

  if (lang === "JavaScript") {
    const headers = [];
    if (t.auth) headers.push(`    "X-API-KEY": "${key}"`);
    if (bodyStr) headers.push(`    "Content-Type": "application/json"`);
    return `const res = await fetch("${url}", {
  method: "${t.method}",
  headers: {
${headers.join(",\n")}
  },${bodyStr ? `\n  body: JSON.stringify(${bodyStr.replace(/\n/g, "\n  ")}),` : ""}
});
const data = await res.json();
console.log(data);`;
  }

  if (lang === "Python") {
    const headers = [];
    if (t.auth) headers.push(`    "X-API-KEY": "${key}"`);
    const pyBody = t.body ? JSON.stringify(t.body, null, 4).replace(/\n/g, "\n") : null;
    return `import requests

res = requests.${t.method.toLowerCase()}(
    "${url}",${headers.length ? `\n    headers={\n${headers.join(",\n")}\n    },` : ""}${pyBody ? `\n    json=${pyBody.replace(/\n/g, "\n    ")},` : ""}
)
print(res.json())`;
  }

  // PHP
  const phpHeaders = [];
  if (t.auth) phpHeaders.push(`    "X-API-KEY: ${key}"`);
  if (bodyStr) phpHeaders.push(`    "Content-Type: application/json"`);
  return `<?php
$ch = curl_init("${url}");
curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_CUSTOMREQUEST  => "${t.method}",
    CURLOPT_HTTPHEADER     => [
${phpHeaders.join(",\n")}
    ],${bodyStr ? `\n    CURLOPT_POSTFIELDS     => '${JSON.stringify(t.body)}',` : ""}
]);
$response = curl_exec($ch);
curl_close($ch);
echo $response;`;
}

const TASKS = [
  { id: "listFaqs", label: "List FAQs" },
  { id: "createFaq", label: "Create an FAQ" },
  { id: "listTickets", label: "List support tickets" },
  { id: "askChat", label: "Ask the AI chat (public)" },
];

const SECTIONS = [
  { id: "quickstart", label: "Quickstart" },
  { id: "auth", label: "Which auth to use" },
  { id: "examples", label: "Code examples" },
  { id: "recipes", label: "Recipes" },
  { id: "troubleshooting", label: "Troubleshooting" },
  { id: "security", label: "Security" },
];

function SectionTitle({ id, icon: Icon, children }) {
  return (
    <div id={`guide-${id}`} className="flex items-center gap-2 border-b border-gray-100 pb-2 mb-4 scroll-mt-24">
      <div className="p-1.5 bg-black rounded-lg">
        <Icon className="h-4 w-4 text-white" />
      </div>
      <h3 className="text-base sm:text-lg font-bold text-gray-900">{children}</h3>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0 h-7 w-7 rounded-full bg-black text-white text-xs font-bold flex items-center justify-center">{n}</div>
      <div className="flex-1 min-w-0 space-y-2">
        <p className="text-sm font-semibold text-gray-900">{title}</p>
        {children}
      </div>
    </div>
  );
}

export default function ApiGuide() {
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const [copied, setCopied] = useState(null);
  const [task, setTask] = useState("listFaqs");
  const [lang, setLang] = useState("cURL");

  const orgId = organization?.id || "org_your_org_id";
  const scrollTo = (id) => document.getElementById(`guide-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const widgetEmbed = `<script src="${process.env.REACT_APP_BACKEND_URL || BASE_URL}/api/widgets/faq-widget.js?organization_id=${orgId}&widget_id=YOUR_WIDGET_ID"></script>`;

  const syncRecipe = `// Run on your server (Node 18+). Keeps Uttik FAQs in sync with your own data.
const BASE_URL = "${BASE_URL}";
const API_KEY = process.env.UTTIK_API_KEY; // never hard-code or ship to the browser

async function uttik(path, { method = "GET", body } = {}) {
  const res = await fetch(BASE_URL + path, {
    method,
    headers: { "X-API-KEY": API_KEY, "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(res.status + " " + (data.message || "request failed"));
  return data;
}

async function syncFaqs(myFaqs) {
  for (const f of myFaqs) {
    try {
      await uttik("/api/faqs/create", {
        method: "POST",
        body: { question: f.question, answer: f.answer, status: "public", tags: f.tags || [] },
      });
    } catch (e) {
      // A 409 / FAQ_DUPLICATE means the question already exists - safe to skip
      console.warn("Skipped:", f.question, e.message);
    }
  }
}`;

  const ticketsRecipe = `// Poll for new tickets and forward them to your CRM / helpdesk (server-side).
async function pullTickets() {
  const { queries } = await uttik("/api/support/queries?limit=30");
  const open = queries.filter((q) => q.status === "open");
  for (const q of open) {
    await sendToMyCrm({ id: q.ticket_id, email: q.email, message: q.message });
  }
  // Mark them as handled in Uttik
  if (open.length) {
    await uttik("/api/support/update/status", {
      method: "PATCH",
      body: { support_queryIds: open.map((q) => q.query_id), status: "responded" },
    });
  }
}
setInterval(pullTickets, 5 * 60 * 1000); // every 5 minutes`;

  const chatRecipe = `// Build your own chat UI. This endpoint is public: only the org ID is needed,
// so it is safe to call from the browser.
const history = [];

async function ask(question) {
  const res = await fetch("${BASE_URL}/api/chat/query/public", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      organization_id: "${orgId}",
      query: question,
      chat_history: history,
    }),
  });
  const data = await res.json();
  history.push({ role: "user", content: question });
  history.push({ role: "assistant", content: data.response });
  return data; // { response, suggested_questions, confidence }
}`;

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* Header */}
      <div>
        <button
          onClick={() => navigate("/api-keys")}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-900 mb-2"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to API Keys
        </button>
        <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent leading-tight">
          API Integration Guide
        </h2>
        <p className="text-xs sm:text-sm text-gray-500 mt-1">
          Go from a new API key to a working integration in a few minutes.
        </p>
      </div>

      {/* In-page nav */}
      <div className="flex flex-wrap gap-2">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            onClick={() => scrollTo(s.id)}
            className="px-3 py-1.5 rounded-full border border-gray-200 bg-white text-xs font-semibold text-gray-700 hover:bg-gray-50"
          >
            {s.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => navigate("/api-keys#api-reference", { state: { openDocs: true } })}
          className="px-3 py-1.5 rounded-full bg-black text-white text-xs font-semibold hover:bg-gray-800"
        >
          Full endpoint reference →
        </button>
      </div>

      {/* Quickstart */}
      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6 space-y-5">
        <SectionTitle id="quickstart" icon={Rocket}>Quickstart</SectionTitle>

        <Step n={1} title="Create an API key">
          <p className="text-xs sm:text-sm text-gray-600">
            Open <button onClick={() => navigate("/api-keys")} className="underline font-semibold text-gray-900">API Keys</button> and
            click <strong>Create Key</strong>. Copy the key right away — it is shown only once. Keys start with <code className="bg-gray-100 px-1 rounded">utsk_</code>.
          </p>
        </Step>

        <Step n={2} title="Make your first request">
          <p className="text-xs sm:text-sm text-gray-600">Run this in a terminal, replacing the key. It returns your organization details if everything is set up correctly.</p>
          <CodeBlock
            id="qs-curl"
            label="cURL"
            copied={copied}
            setCopied={setCopied}
            code={`curl "${BASE_URL}/api/org/" \\\n  -H "X-API-KEY: utsk_your_key_here"`}
          />
        </Step>

        <Step n={3} title="Build something">
          <p className="text-xs sm:text-sm text-gray-600">
            Pick a recipe below — sync FAQs from your own system, pull support tickets into your CRM, or build a custom chat UI.
            Your organization is detected from the key, so you never need to send an org ID for key-protected endpoints.
          </p>
          <p className="text-xs text-gray-500">
            Your org ID (for public endpoints): <code className="bg-gray-100 px-1 rounded break-all">{orgId}</code>
          </p>
        </Step>
      </section>

      {/* Auth */}
      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6">
        <SectionTitle id="auth" icon={KeyRound}>Which authentication do I use?</SectionTitle>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs sm:text-sm">
            <thead>
              <tr className="text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200">
                <th className="py-2 pr-4">Type</th>
                <th className="py-2 pr-4">What you send</th>
                <th className="py-2 pr-4">Where to call from</th>
                <th className="py-2">Used for</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-gray-700 align-top">
              <tr>
                <td className="py-2.5 pr-4"><span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 font-semibold">API Key</span></td>
                <td className="py-2.5 pr-4"><code className="bg-gray-100 px-1 rounded">X-API-KEY</code> header</td>
                <td className="py-2.5 pr-4">Your server only</td>
                <td className="py-2.5">Managing FAQs, widgets, tickets, categories, tags, analytics, org details</td>
              </tr>
              <tr>
                <td className="py-2.5 pr-4"><span className="text-[10px] px-1.5 py-0.5 rounded bg-green-50 text-green-700 font-semibold">Public</span></td>
                <td className="py-2.5 pr-4"><code className="bg-gray-100 px-1 rounded">organization_id</code> only</td>
                <td className="py-2.5 pr-4">Browser or server</td>
                <td className="py-2.5">Public FAQ lists, chat widget queries, support form submissions, FAQ feedback</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-500 mt-3">Each endpoint in the reference is tagged <strong>API Key</strong> or <strong>Public</strong>.</p>
      </section>

      {/* Examples */}
      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6 space-y-4">
        <SectionTitle id="examples" icon={Code2}>Code examples</SectionTitle>
        <div className="flex flex-wrap gap-2">
          {TASKS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTask(t.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${task === t.id ? "bg-black text-white border-black" : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex gap-1 border-b border-gray-200">
          {LANGS.map((l) => (
            <button
              key={l}
              onClick={() => setLang(l)}
              className={`px-3 py-1.5 text-xs font-semibold -mb-px border-b-2 ${lang === l ? "border-black text-gray-900" : "border-transparent text-gray-500 hover:text-gray-800"}`}
            >
              {l}
            </button>
          ))}
        </div>
        <CodeBlock
          id={`ex-${task}-${lang}`}
          label={`${lang} · ${TASKS.find((t) => t.id === task)?.label}`}
          copied={copied}
          setCopied={setCopied}
          code={buildSample(task, lang, orgId)}
        />
      </section>

      {/* Recipes */}
      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6 space-y-6">
        <SectionTitle id="recipes" icon={Lightbulb}>Recipes</SectionTitle>

        <div className="space-y-2">
          <h4 className="text-sm font-bold text-gray-900">1. Add an FAQ widget to your website (no API key needed)</h4>
          <p className="text-xs sm:text-sm text-gray-600">
            Create a widget in <button onClick={() => navigate("/widgets")} className="underline font-semibold text-gray-900">Widget Manager</button>, then
            paste this script before <code className="bg-gray-100 px-1 rounded">&lt;/body&gt;</code>. The Widget Manager also offers an SEO-friendly iframe embed.
          </p>
          <CodeBlock id="rc-widget" label="HTML" copied={copied} setCopied={setCopied} code={widgetEmbed} />
        </div>

        <div className="space-y-2">
          <h4 className="text-sm font-bold text-gray-900">2. Sync FAQs from your own system</h4>
          <p className="text-xs sm:text-sm text-gray-600">
            Push FAQs from your CMS, helpdesk or spreadsheet. Creating a question that already exists returns a duplicate error, so re-running the script is safe.
          </p>
          <CodeBlock id="rc-sync" label="Node.js (server)" copied={copied} setCopied={setCopied} code={syncRecipe} />
        </div>

        <div className="space-y-2">
          <h4 className="text-sm font-bold text-gray-900">3. Send support tickets to your CRM</h4>
          <p className="text-xs sm:text-sm text-gray-600">Pull open tickets on a schedule, forward them, then update their status.</p>
          <CodeBlock id="rc-tickets" label="Node.js (server)" copied={copied} setCopied={setCopied} code={ticketsRecipe} />
        </div>

        <div className="space-y-2">
          <h4 className="text-sm font-bold text-gray-900">4. Build a custom chat experience</h4>
          <p className="text-xs sm:text-sm text-gray-600">Use the public chat endpoint with your own UI. Pass the running conversation as <code className="bg-gray-100 px-1 rounded">chat_history</code>.</p>
          <CodeBlock id="rc-chat" label="JavaScript (browser or server)" copied={copied} setCopied={setCopied} code={chatRecipe} />
        </div>
      </section>

      {/* Troubleshooting */}
      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6">
        <SectionTitle id="troubleshooting" icon={Wrench}>Troubleshooting</SectionTitle>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs sm:text-sm">
            <thead>
              <tr className="text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider border-b border-gray-200">
                <th className="py-2 pr-4">Problem</th>
                <th className="py-2">What to check</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-gray-700 align-top">
              <tr>
                <td className="py-2.5 pr-4 font-semibold">Authentication error / 401</td>
                <td className="py-2.5">Header must be exactly <code className="bg-gray-100 px-1 rounded">X-API-KEY</code>. Check the key was copied in full, has not been deleted, and has not passed its expiry date (keys last 365 days).</td>
              </tr>
              <tr>
                <td className="py-2.5 pr-4 font-semibold">&quot;Organization not found&quot;</td>
                <td className="py-2.5">For public endpoints, make sure <code className="bg-gray-100 px-1 rounded">organization_id</code> matches the Org ID shown at the top of the API Keys page.</td>
              </tr>
              <tr>
                <td className="py-2.5 pr-4 font-semibold">FAQ_DUPLICATE (409)</td>
                <td className="py-2.5">A similar question already exists. Use <code className="bg-gray-100 px-1 rounded">PUT /api/faqs/update</code> instead, or skip it.</td>
              </tr>
              <tr>
                <td className="py-2.5 pr-4 font-semibold">&quot;not found&quot; on update/delete</td>
                <td className="py-2.5">The ID belongs to another organization or was already deleted. List first and use the returned IDs.</td>
              </tr>
              <tr>
                <td className="py-2.5 pr-4 font-semibold">Batch size limit exceeded</td>
                <td className="py-2.5"><code className="bg-gray-100 px-1 rounded">/api/faqs/optimize</code> accepts at most 50 FAQs per call. Split larger jobs into chunks.</td>
              </tr>
              <tr>
                <td className="py-2.5 pr-4 font-semibold">Key stopped working suddenly</td>
                <td className="py-2.5">Deleting a key revokes it immediately. Create a new key and update your application.</td>
              </tr>
              <tr>
                <td className="py-2.5 pr-4 font-semibold">Request blocked in the browser</td>
                <td className="py-2.5">API-key endpoints are meant for server-side use. Move the call to your backend instead of calling it from page JavaScript.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-500 mt-3">Still stuck? Contact support from the dashboard and include the endpoint, the time of the request and the error message.</p>
      </section>

      {/* Security */}
      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6">
        <SectionTitle id="security" icon={ShieldCheck}>Security best practices</SectionTitle>
        <ul className="list-disc pl-5 space-y-1.5 text-xs sm:text-sm text-gray-700">
          <li>Keep API keys on your server. Never put them in front-end code, mobile apps, public repositories or screenshots.</li>
          <li>Store keys in environment variables or a secrets manager, not in source code.</li>
          <li>Create one key per application or environment (for example <code className="bg-gray-100 px-1 rounded">production-site</code>, <code className="bg-gray-100 px-1 rounded">staging</code>) so you can revoke one without breaking the others.</li>
          <li>If a key might be exposed, delete it and create a new one straight away. Deleting is immediate.</li>
          <li>Use public endpoints for anything that runs in the visitor&apos;s browser. They only need your organization ID.</li>
          <li>Rotate keys before they expire so your integration doesn&apos;t stop unexpectedly.</li>
        </ul>
      </section>

      {/* CTA → full endpoint reference */}
      <section className="bg-gray-900 rounded-xl shadow-sm p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-base sm:text-lg font-bold text-white">Full endpoint reference</h3>
          <p className="text-xs sm:text-sm text-gray-300 mt-1">
            Every API-key and public endpoint with request bodies, success responses, and error responses.
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/api-keys#api-reference", { state: { openDocs: true } })}
          className="flex-shrink-0 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white text-gray-900 text-sm font-semibold hover:bg-gray-100 transition-colors"
        >
          Open endpoint reference →
        </button>
      </section>
    </div>
  );
}
